import { ThunkAction } from 'redux-thunk';
import { Action, Dispatch, ActionCreator } from 'redux';

import { AxiosError } from 'axios';
import { AppState } from '../index';
import {
  Action as FetchAction,
  ActionTypes,
  SetUnlockDataActionPayload,
  SetUnlockDataAction,
} from './types';
import {
  fetch,
  dispense,
  cancelDispensationItem,
} from '../../services/prescription';
import { fetchHistoryAndItemsStatus } from '../../services/history';
import PrescriptionInterface, {
  PrescriptionItemInterface,
} from '../../interfaces/prescription';
import HistoryInterface from '../../interfaces/history';

type FetchPrescriptionThunk = ThunkAction<Promise<PrescriptionInterface | null>, AppState, null, Action<FetchAction>>;

export const fetchPrescription = (
  token: string,
): FetchPrescriptionThunk => async (
  dispatch: Dispatch<FetchAction>,
): Promise<PrescriptionInterface | null> => {
  try {
    dispatch({
      type: ActionTypes.FETCH,
    });

    const prescriptionResponse = await fetch(token);

    dispatch({
      type: ActionTypes.FETCH_SUCCESS,
      prescription: prescriptionResponse,
    });

    return prescriptionResponse;
  } catch (catchError) {
    const error = catchError as AxiosError;

    const errorStatus =
      error.response && error.response.status ? error.response.status : 0;

    dispatch({
      type: ActionTypes.FETCH_ERROR,
      status: errorStatus,
    });

    return null;
  }
};

export const dispensePrescription: ActionCreator<ThunkAction<void, AppState, null, Action<FetchAction>>> =
  (prescription: PrescriptionInterface | null) =>
    (
      dispatch: Dispatch<FetchAction>,
    ): Promise<PrescriptionInterface | string[]> | void => {
      if (!prescription) {
        return;
      }

      if (
        prescription.items.length === 0 ||
        !prescription.items.some((i) => i.checked === true)
      ) {
        return;
      }

      dispatch({
        type: ActionTypes.DISPENSE,
      });

      dispense(prescription).then(
        (prescriptionResponse: PrescriptionInterface | string[]) => {
          dispatch({
            type: ActionTypes.DISPENSE_SUCCESS,
            prescription: prescriptionResponse as PrescriptionInterface,
          });

          return prescriptionResponse;
        },
        (errors: string[]) => {
          dispatch({
            type: ActionTypes.DISPENSE_ERROR,
            errors,
          });

          return errors;
        },
      );
    };

export const cancelDispensation: ActionCreator<ThunkAction<void, AppState, null, Action<FetchAction>>> =
  (id: number, token: string) =>
    (dispatch: Dispatch<FetchAction>): Promise<PrescriptionInterface> => {
      dispatch({
        type: ActionTypes.DISPENSE_CANCEL,
      });

      return cancelDispensationItem(id, token).then(
        (prescription: PrescriptionInterface) => {
          dispatch({
            type: ActionTypes.DISPENSE_CANCEL_SUCCESS,
            prescription,
          });

          return prescription;
        },
      );
    };

export const toggleItem: ActionCreator<ThunkAction<void, AppState, null, Action<FetchAction>>> =
  ({ itemId, selectedQuantity }: { itemId: number, selectedQuantity: number | 'all' }) =>
    (
      dispatch: Dispatch<FetchAction>,
      getState: Function,
    ): Promise<void> | void => {
      const { items } = getState().prescription.prescription;

      const newItemsState = items.map((item: PrescriptionItemInterface) => {
        if (itemId === item.id) {
          return {
            ...item,
            checked: !item.checked,
            selectedQuantity: !item.checked ? selectedQuantity : item.selectedQuantity,
          };
        }

        return item;
      });

      dispatch({
        type: ActionTypes.UPDATE_ITEMS,
        items: newItemsState,
      });

      return newItemsState;
    };

export const updateItemQuantity: ActionCreator<ThunkAction<void, AppState, null, Action<FetchAction>>> =
  (itemId: number, quantity: number | 'all') =>
    (dispatch: Dispatch<FetchAction>, getState: Function): void => {
      dispatch({
        type: ActionTypes.UPDATE_ITEM_QUANTITY,
        itemId,
        quantity,
      });
    };

export const getItemById: ActionCreator<ThunkAction<void, AppState, null, Action>> =
  (id: number) =>
    (
      dispatch: Dispatch<FetchAction>,
      getState: Function,
    ): Promise<void> | void => {
      const { items } = getState().prescription.prescription;

      return items.find((item: PrescriptionItemInterface) => {
        return item.id === id;
      });
    };

export const setUnlockData = (
  payload: SetUnlockDataActionPayload,
): SetUnlockDataAction => ({
  type: ActionTypes.SET_UNLOCK_DATA,
  payload,
});

export const fetchHistory: ActionCreator<ThunkAction<void, AppState, null, Action<FetchAction>>> =
  (prescriptionToken: string) =>
    async (dispatch: Dispatch<FetchAction>): Promise<HistoryInterface[]> => {
      try {
        dispatch({
          type: ActionTypes.FETCH_HISTORY,
        });

        const response = await fetchHistoryAndItemsStatus(prescriptionToken);

        dispatch({
          type: ActionTypes.FETCH_HISTORY_SUCCESS,
          history: response.history,
          itemsStatus: response.itemsStatus,
          showFullPrescriptionDownloadButton: response.showFullPrescriptionDownloadButton,
        });

        return [];
      } catch (error) {
        dispatch({
          type: ActionTypes.FETCH_HISTORY_ERROR,
        });

        return [];
      }
    };
